import "./Asset.scss";

import React from "react";
import clsx from "clsx";

import { VisibleContext } from "../../../lib/util/ReactUtils";

export default class Asset extends React.PureComponent {
	static contextType = VisibleContext;
	static globalLoaded = new Set();

	shown = false;

	image = React.createRef();

	state = {
		currentSrc: null,
		srcLoaded: false,
		srcErrored: false,
		placeholderLoaded: false,
	};

	static create(baseProps) {
		return (props) => {
			return <Asset {...baseProps} {...props} />;
		};
	}

	static preload(src) {
		const image = new Image();
		image.src = src;
		image.onload = () => Asset.globalLoaded.add(src);
	}

	static isLoaded(src) {
		return Asset.globalLoaded.has(src);
	}

	async componentDidMount() {
		const { src } = this.props;
		if (this.image.current && this.image.current.complete) {
			Asset.globalLoaded.add(src);
			this.forceUpdate();
		}
	}

	placeholderLoaded = (e) => {
		this.setState({ placeholderLoaded: true });
	};

	loaded = (e) => {
		const loadedSrc = this.getLoadedSrc();
		this.setState({ srcLoaded: true, currentSrc: loadedSrc }, () => {
			Asset.globalLoaded.add(loadedSrc);
		});
	};

	getLoadedSrc = () => {
		const srcs = [this.props.src, ...Object.values(this.props.sources || {})];
		return this.image.current
			? srcs.find((src) => this.image.current.src.includes(src))
			: null;
	};

	errored = (e) => {
		console.error(
			`Failed to load asset: ${e.target.src} (from ${
				this.state.currentSrc || this.props.src
			})`,
		);
		this.setState({ srcErrored: true });
	};

	render() {
		const { srcLoaded, placeholderLoaded, srcErrored, currentSrc } = this.state;
		const { src, sources, alt, className, children, style, placeholder } =
			this.props;

		const loaded = Asset.isLoaded(this.getLoadedSrc() || src);
		const classes = {
			"--load": (srcLoaded || placeholderLoaded) && !loaded,
			"--loaded": loaded,
			"--error": srcErrored,
			"--placeholder": placeholderLoaded,
		};

		if (this.context) {
			this.shown = true;
		}

		return (
			<span className={clsx("Asset", className, classes)}>
				{this.shown && (
					<>
						{placeholder && (
							<img
								style={style}
								onLoad={this.placeholderLoaded}
								className={clsx("Asset__image", "Asset__placeholder", {
									"--visible": placeholderLoaded,
								})}
								src={placeholder}
								alt={`${alt} placeholder`}
							/>
						)}
						<picture>
							{sources &&
								Object.keys(sources).map((type) => (
									<source
										key={type}
										type={`image/${type}`}
										srcSet={sources[type]}
									/>
								))}
							<img
								style={style}
								onLoad={this.loaded}
								onError={this.errored}
								ref={this.image}
								className={clsx("Asset__image", classes)}
								src={src}
								alt={alt || "Asset"}
							/>
						</picture>

						{children && <span className={"Asset__content"}>{children}</span>}
					</>
				)}
			</span>
		);
	}
}
